// src/pages/Projects/ProjectTabs.tsx

import React, { useState, useEffect } from 'react';
import { Tabs } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../stores/root-store-context';
import { FileTextOutlined, UnorderedListOutlined, FileOutlined } from '@ant-design/icons';
import { slugify } from '../../routes/RouterUtilities';
import { TariffPlan } from '../../enums/TariffPlan';

interface ProjectTabsProps {
  isAnonymous?: boolean;
  onTabChange?: (key: string) => void;
}

const ProjectTabs = observer(({isAnonymous = false, onTabChange}: ProjectTabsProps) => {
 const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationStore, projectsStore } = useStore();
  const companyTariffPlan = applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff;

  // Get URL parameters
  const { projectSlug, projectId, id } = useParams();

  // Internal state to track the project ID and slug
  const [currentProjectId, setCurrentProjectId] = useState<string | undefined>();
  const [currentProjectSlug, setCurrentProjectSlug] = useState<string | undefined>();
  
  // Determine active tab based on current URL
  const [activeKey, setActiveKey] = useState('info');

  // Load project details when slug changes
  useEffect(() => {
    const loadProjectDetails = async () => {
      // Determine which parameter to use
      const projectIdentifier = projectSlug || projectId || id;
      
      if (projectIdentifier) {
        try {
          // Get project - either by slug or by ID
          const project = await projectsStore.getBySlug(projectIdentifier);
          
          if (project) {
            setCurrentProjectId(project.id.toString());
            setCurrentProjectSlug(slugify(project.title || project.name));
          }
        } catch (error) {
          console.error('Error loading project details:', error);
        }
      }
    };
    
    loadProjectDetails();
  }, [projectSlug, projectId, id, projectsStore]);

  // Update active key when location changes
  useEffect(() => {
    if (location.pathname.includes('tasks')) {
      setActiveKey('tasks');
    } else if (location.pathname.includes('files')) {
      setActiveKey('files');
    } else {
      setActiveKey('info');
    }
  }, [location.pathname]);

  const handleTabChange = (key: any) => {
    setActiveKey(key);
    onTabChange?.(key); 
    if (isAnonymous) {
      const token = new URLSearchParams(location.search).get('token');
      switch (key) {
        case 'tasks':
          navigate(`/anonymous/project/tasks/?token=${token}`);
          break;
        case 'info':
        default:
          navigate(`/anonymous/project/?token=${token}`);
          break;
      }
    } else {
      if (!currentProjectSlug) {
        console.error('Project slug is undefined');
        return;
      }
      const tenant = applicationStore.company?.Tenant || '';
      // Navigate to the appropriate route with slug
      switch (key) {
        case 'tasks':
          navigate(`/${tenant}/projects/${currentProjectSlug}/tasks`);
          break;
        case 'files':
          navigate(`/${tenant}/projects/${currentProjectSlug}/files`);
          break;
        case 'info':
        default:
          navigate(`/${tenant}/projects/${currentProjectSlug}`);
          break;
      }
    }
  };

  const items = [
    {
      key: 'info',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '15px' }}>
          <FileTextOutlined />
          {t('ProjectDescription')}
        </span>
      ),
    },
  ];

  if (
    !isAnonymous &&
    companyTariffPlan !== TariffPlan.Basic &&
    companyTariffPlan !== TariffPlan.Annual
  ) {
    items.push({
      key: 'tasks',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '15px' }}>
          <UnorderedListOutlined />
          {t('ProjectTasks')}
        </span>
      ),
    });
    items.push({
      key: 'files',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '15px' }}>
          <FileOutlined />
          {t('ProjectFiles')}
        </span>
      ),
    });
  }

  if (isAnonymous) {
    // Anonymous can only see info and tasks
    items.push({
      key: 'tasks',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '15px' }}>
          <UnorderedListOutlined />
          {t('ProjectTasks')}
        </span>
      ),
    });
  }

  return (
    <div className="project-tabs-container">
      <style>
        {`
          /* Remove default tab bottom border that might cause duplication */
          .project-tabs-container .ant-tabs-nav::before {
            border-bottom: none !important;
          }
          
          /* Add single clear border after tabs */
          .project-tabs-container .ant-tabs-nav {
            border-bottom: 1px solid #e8e8e8;
            margin-bottom: 16px;
          }
          
          .project-tabs-container .ant-tabs-tab {
            border: 1px solid #e8e8e8 !important;
            border-bottom: none !important;
            border-radius: 4px 4px 0 0;
            background-color: #fafafa;
            margin-right: 2px;
            padding: 8px 16px;
          }
          
          .project-tabs-container .ant-tabs-tab-active {
            background-color: #fff !important;
            z-index: 1;
          }
          
          /* Ensure tab content doesn't have additional borders */
          .project-tabs-container .ant-tabs-content {
            border-top: none;
            margin-top: 0;
          }
        `}
      </style>
      <Tabs 
        activeKey={activeKey} 
        onChange={handleTabChange}
        items={items}
        size="large"
        type="card"
      />
    </div>
  );
});

export default ProjectTabs;